import React, { useState } from "react";
import { Button, Modal, Upload, Progress } from "antd";
import {
  InboxOutlined,
  FileOutlined,
  CloseOutlined,
  ReloadOutlined,
  StopOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import JSZip from "jszip";
import axios from "axios";
import { request } from "../../api";
import { ALLOWED_UPLOAD_EXTENSIONS } from "../../config/constants";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "./AddFile.module.css";

const { Dragger } = Upload;

type FileStatus = "pending" | "extracting" | "uploading" | "done" | "error" | "cancelled" | "collision";

interface FileEntry {
  id: string;
  file: File;
  name: string;
  status: FileStatus;
  progress: number;
  isZip?: boolean;
  zipChoiceMade?: boolean;
  extractAfterUpload?: boolean;
  error?: string;
  abortController?: AbortController;
}

const AddFile = ({ isOpen, onClose, id, refetch, mode = "db", prefix = "" }: any) => {
  const [fileList, setFileList] = useState<FileEntry[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [replaceConfirmEntry, setReplaceConfirmEntry] = useState<FileEntry | null>(null);
  const [batchCollisionFiles, setBatchCollisionFiles] = useState<FileEntry[]>([]);
  const [showReplaceAllConfirm, setShowReplaceAllConfirm] = useState(false);
  const cancelAllRef = React.useRef(false);
  const isS3 = mode === "s3";

  // Generate a unique ID for each file entry
  const generateId = () => Math.random().toString(36).substring(2, 9);

  // Handle files dropped or selected via Dragger
  const handleBeforeUpload = (file: File) => {
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!ALLOWED_UPLOAD_EXTENSIONS.includes(ext)) {
      toast.error(`${file.name} — file type not allowed`);
      return false; // Prevent auto-upload and skip adding to state
    }

    const isZip = file.name.toLowerCase().endsWith(".zip");
    setFileList((prev) => [
      ...prev,
      {
        id: generateId(),
        file,
        name: file.name,
        status: "pending",
        progress: 0,
        isZip,
        zipChoiceMade: !isZip,
      },
    ]);
    return false; // Prevent auto-upload
  };

  const handleRemoveFile = (entryId: string) => {
    setFileList((prev) => prev.filter((item) => item.id !== entryId));
  };

  // Handle Zip "Upload as-is" choice
  const handleZipAsIs = (entryId: string) => {
    setFileList((prev) =>
      prev.map((item) =>
        item.id === entryId ? { ...item, zipChoiceMade: true } : item
      )
    );
  };

  // Handle Zip "Extract" choice
  const handleZipExtract = async (entry: FileEntry) => {
    if (isS3) {
      // S3 mode: Server-side extraction after upload
      setFileList((prev) =>
        prev.map((item) =>
          item.id === entry.id ? { ...item, zipChoiceMade: true, extractAfterUpload: true } : item
        )
      );
      return;
    }

    // DB mode: Client-side extraction
    // Mark as extracting
    setFileList((prev) =>
      prev.map((item) =>
        item.id === entry.id ? { ...item, status: "extracting" } : item
      )
    );

    try {
      const zip = await JSZip.loadAsync(entry.file);
      const extractedFiles: FileEntry[] = [];

      const fileNamesCounts: Record<string, number> = {};

      for (const [relativePath, zipEntry] of Object.entries(zip.files)) {
        if (!zipEntry.dir) {
          const blob = await zipEntry.async("blob");
          let baseName = relativePath.split("/").pop() || "unknown_file";

          // Handle duplicate names
          if (fileNamesCounts[baseName] !== undefined) {
            fileNamesCounts[baseName]++;
            const nameParts = baseName.split(".");
            const ext = nameParts.length > 1 ? `.${nameParts.pop()}` : "";
            baseName = `${nameParts.join(".")}_${fileNamesCounts[baseName]}${ext}`;
          } else {
            fileNamesCounts[baseName] = 0;
          }

          const newFile = new File([blob], baseName, {
            type: blob.type || "application/octet-stream",
          });
          extractedFiles.push({
            id: generateId(),
            file: newFile,
            name: baseName,
            status: "pending",
            progress: 0,
            isZip: false,
            zipChoiceMade: true,
          });
        }
      }

      // Replace the zip entry with the extracted files
      setFileList((prev) => {
        const index = prev.findIndex((item) => item.id === entry.id);
        if (index === -1) return prev;
        const newList = [...prev];
        newList.splice(index, 1, ...extractedFiles);
        return newList;
      });
    } catch (err: any) {
      toast.error(`Failed to extract ${entry.name}: ${err.message}`);
      // Revert to pending but mark zip choice as made so it can be uploaded as-is or removed
      setFileList((prev) =>
        prev.map((item) =>
          item.id === entry.id
            ? { ...item, status: "pending", zipChoiceMade: false }
            : item
        )
      );
    }
  };

  const uploadSingleFile = async (entry: FileEntry): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      const abortController = new AbortController();
      setFileList((prev) =>
        prev.map((item) =>
          item.id === entry.id ? { ...item, status: "uploading", progress: 0, abortController } : item
        )
      );

      const formData = new FormData();
      formData.append("file", entry.file);

      // Call API directly to pass onUploadProgress
      request
        .post(`/web-twinprocms/file-library/${id ? id : ""}/create`, formData, {
          signal: abortController.signal,
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;
            setFileList((prev) =>
              prev.map((item) =>
                item.id === entry.id ? { ...item, progress: percentCompleted } : item
              )
            );
          },
        })
        .then(() => {
          setFileList((prev) =>
            prev.map((item) =>
              item.id === entry.id ? { ...item, status: "done", progress: 100 } : item
            )
          );
          resolve(true);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            setFileList((prev) =>
              prev.map((item) =>
                item.id === entry.id ? { ...item, status: "cancelled", error: undefined } : item
              )
            );
          } else {
            setFileList((prev) =>
              prev.map((item) =>
                item.id === entry.id
                  ? {
                      ...item,
                      status: "error",
                      error: err.response?.data?.file?.[0] || err.message,
                    }
                  : item
              )
            );
          }
          resolve(false); // Resolve anyway so loop continues
        });
    });
  };

  const getContentType = (file: File) => {
    if (file.type) return file.type;
    const ext = file.name.split('.').pop()?.toLowerCase();
    const mimeTypes: Record<string, string> = {
      'js': 'application/javascript',
      'mjs': 'application/javascript',
      'wasm': 'application/wasm',
      'json': 'application/json',
      'html': 'text/html',
      'css': 'text/css',
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'svg': 'image/svg+xml',
      'gif': 'image/gif',
      'webp': 'image/webp',
      'mp4': 'video/mp4',
      'pdf': 'application/pdf',
      'zip': 'application/zip',
      'xml': 'application/xml',
      'txt': 'text/plain',
      'csv': 'text/csv'
    };
    return (ext && mimeTypes[ext]) || 'application/octet-stream';
  };

  const uploadSingleFileS3 = async (entry: FileEntry, uploadUrl: string): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      const abortController = new AbortController();
      setFileList((prev) =>
        prev.map((item) =>
          item.id === entry.id ? { ...item, status: "uploading", progress: 0, abortController } : item
        )
      );

      axios.put(uploadUrl, entry.file, {
        signal: abortController.signal,
        headers: {
          "Content-Type": getContentType(entry.file)
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setFileList((prev) =>
            prev.map((item) =>
              item.id === entry.id ? { ...item, progress: percentCompleted } : item
            )
          );
        },
      })
      .then(() => {
        setFileList((prev) =>
          prev.map((item) =>
            item.id === entry.id ? { ...item, status: "done", progress: 100 } : item
          )
        );
        resolve(true);
      })
      .catch((err) => {
        if (axios.isCancel(err)) {
          setFileList((prev) =>
            prev.map((item) =>
              item.id === entry.id ? { ...item, status: "cancelled", error: undefined } : item
            )
          );
        } else {
          setFileList((prev) =>
            prev.map((item) =>
              item.id === entry.id
                ? {
                    ...item,
                    status: "error",
                    error: err.response?.data || err.message || "Upload failed",
                  }
                : item
            )
          );
        }
        resolve(false); // Resolve anyway so loop continues
      });
    });
  };

  const processS3Upload = async (entry: FileEntry, uploadUrlInfo: {url: string, key: string}): Promise<boolean> => {
    const success = await uploadSingleFileS3(entry, uploadUrlInfo.url);
    if (success && entry.extractAfterUpload) {
      if (cancelAllRef.current) return false;
      const extractAbortController = new AbortController();
      setFileList(prev => prev.map(item => item.id === entry.id ? { ...item, status: "extracting", abortController: extractAbortController } : item));
      try {
        const extractRes = await request.post("/web-twinprocms/s3-file/extract", {
          zip_key: uploadUrlInfo.key,
          extract_prefix: prefix
        }, { signal: extractAbortController.signal });
        
        const extractedFiles = extractRes.data?.data?.extracted_files || [];
        
        setFileList(prev => {
          const idx = prev.findIndex(item => item.id === entry.id);
          if (idx === -1) return prev;
          const newEntries = extractedFiles.map((ef: any) => ({
            id: generateId(),
            file: new File([], ef.name),
            name: ef.name,
            status: "done",
            progress: 100,
            isZip: false,
            zipChoiceMade: true
          }));
          const newList = [...prev];
          newList.splice(idx, 1, ...newEntries);
          return newList;
        });
        return true;
      } catch (err: any) {
        if (axios.isCancel(err)) {
          setFileList(prev => prev.map(item => item.id === entry.id ? { ...item, status: "cancelled", error: "Cancelled (server may still be processing)" } : item));
        } else {
          const errMsg = err.response?.data?.error || err.response?.data?.detail || err.message || "Extraction failed";
          setFileList(prev => prev.map(item => item.id === entry.id ? { ...item, status: "error", error: errMsg } : item));
        }
        return false;
      }
    }
    return success;
  };

  const handleUploadAll = async () => {
    setIsUploading(true);
    cancelAllRef.current = false;
    let successCount = 0;
    let failCount = 0;
    let collisionCount = 0;
    let newBatchCollisionFiles: FileEntry[] = [];

    const getFilesToUpload = () => fileList.filter(
      (f) => (f.status === "pending" || f.status === "error") && f.zipChoiceMade
    );

    if (isS3) {
      const attemptS3Upload = async (files: FileEntry[]) => {
        if (files.length === 0 || cancelAllRef.current) return;
        
        try {
          const presignPayload = {
            prefix,
            files: files.map(f => ({
              filename: f.name,
              content_type: getContentType(f.file)
            }))
          };
          const presignRes = await request.post("/web-twinprocms/s3-file/presign", presignPayload);
          const presignedUrls = presignRes.data?.data || [];
          
          const urlMap: Record<string, { url: string; key: string }> = {};
          presignedUrls.forEach((item: any) => {
            urlMap[item.filename] = { url: item.upload_url, key: item.key };
          });

          for (const entry of files) {
            if (cancelAllRef.current) break;
            const uploadUrlInfo = urlMap[entry.name];
            if (!uploadUrlInfo || !uploadUrlInfo.url) {
              setFileList(prev => prev.map(item => item.id === entry.id ? { ...item, status: "error", error: "Missing presigned URL" } : item));
              failCount++;
              continue;
            }
            const success = await processS3Upload(entry, uploadUrlInfo);
            if (success) successCount++; else failCount++;
          }
        } catch (err: any) {
          const errMsg = err.response?.data?.error || err.message || "Failed to generate upload URLs";
          const collisions: string[] = err.response?.data?.collisions || [];

          if (collisions.length > 0 && err.response?.status === 409) {
            const collidingEntries = files.filter(f => collisions.includes(f.name));
            collisionCount += collidingEntries.length;
            newBatchCollisionFiles = [...newBatchCollisionFiles, ...collidingEntries];
            
            setFileList(prev => prev.map(item => {
              if (collisions.includes(item.name) && files.find(f => f.id === item.id)) {
                return { ...item, status: "collision" as FileStatus, error: "File already exists." };
              }
              return item;
            }));

            // Auto-resume innocent files
            const innocentFiles = files.filter(f => !collisions.includes(f.name));
            if (innocentFiles.length > 0) {
              await attemptS3Upload(innocentFiles);
            }
          } else {
            failCount += files.length;
            setFileList(prev => prev.map(item => {
              if (!files.find(f => f.id === item.id)) return item;
              return { ...item, status: "error", error: errMsg };
            }));
          }
        }
      };

      await attemptS3Upload(getFilesToUpload());

    } else {
      const filesToUpload = getFilesToUpload();
      for (const entry of filesToUpload) {
        if (cancelAllRef.current) break;
        const success = await uploadSingleFile(entry);
        if (success) successCount++; else failCount++;
      }
    }

    setIsUploading(false);

    if (cancelAllRef.current) {
      setFileList(prev => prev.map(item => (item.status === "pending" ? { ...item, status: "cancelled" } : item)));
      toast.warning("Upload queue cancelled.");
      cancelAllRef.current = false;
      return;
    }

    if (newBatchCollisionFiles.length > 0) {
      setBatchCollisionFiles(newBatchCollisionFiles);
    }

    const totalProcessed = successCount + failCount + collisionCount;
    if (totalProcessed > 0) {
      if (failCount === 0 && collisionCount === 0) {
        toast.success(`${successCount} file(s) uploaded successfully.`);
        refetch();
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else if (failCount > 0) {
        toast.warning(`${successCount} uploaded, ${failCount} failed.`);
        refetch();
      } else if (successCount > 0 && collisionCount > 0) {
        toast.success(`${successCount} file(s) uploaded successfully.`);
        refetch();
      }
    }
  };

  const isBusy = isUploading || fileList.some(f => f.status === "extracting");

  const handleClose = () => {
    if (isBusy) return;
    setFileList([]);
    onClose();
  };

  const handleModalCancel = () => {
    if (isBusy) {
      toast.warning("Operation in progress. Please wait for it to finish.");
      return;
    }
    handleClose();
  };

  // Check if we can upload (some pending/error files, and all zips have choices made)
  // "collision" files are excluded — they require explicit per-file "Replace" action
  const allZipChoicesMade = fileList.filter(f => f.status !== "collision").every((f) => f.zipChoiceMade);
  const hasFilesToUpload = fileList.some(
    (f) => f.status === "pending" || f.status === "error"
  );
  const canUpload = !isBusy && hasFilesToUpload && allZipChoicesMade;

  const getStatusText = (status: FileStatus) => {
    switch (status) {
      case "pending":
        return "Pending";
      case "extracting":
        return "Extracting...";
      case "uploading":
        return "Uploading...";
      case "done":
        return "Done";
      case "error":
        return "Failed";
      case "cancelled":
        return "Cancelled";
      case "collision":
        return "Exists";
    }
  };

  // Handle Replace file (overwrite) — sends a presign request for a single file with overwrite=true
  const handleReplaceFile = async (entry: FileEntry) => {
    setReplaceConfirmEntry(null);
    setFileList(prev => prev.map(item => item.id === entry.id ? { ...item, status: "uploading", progress: 0, error: undefined } : item));

    try {
      const presignPayload = {
        prefix,
        files: [{
          filename: entry.name,
          content_type: getContentType(entry.file),
          overwrite: true
        }]
      };
      const presignRes = await request.post("/web-twinprocms/s3-file/presign", presignPayload);
      const presignedUrls = presignRes.data?.data || [];
      if (!presignedUrls.length || !presignedUrls[0].upload_url) {
        setFileList(prev => prev.map(item => item.id === entry.id ? { ...item, status: "error", error: "Failed to get upload URL for replacement." } : item));
        return;
      }
      
      const success = await processS3Upload(entry, { url: presignedUrls[0].upload_url, key: presignedUrls[0].key });
      if (success) {
        toast.success(`${entry.name} replaced successfully.`);
        refetch();
      }
    } catch (err: any) {
      const errMsg = err.response?.data?.error || err.message || "Replace failed";
      setFileList(prev => prev.map(item => item.id === entry.id ? { ...item, status: "error", error: errMsg } : item));
    }
  };

  const handleReplaceAll = async () => {
    setShowReplaceAllConfirm(false);
    setBatchCollisionFiles([]);
    setIsUploading(true);
    cancelAllRef.current = false;
    
    const filesToReplace = [...batchCollisionFiles];
    setFileList(prev => prev.map(item => {
        if (filesToReplace.find(f => f.id === item.id)) {
            return { ...item, status: "uploading", progress: 0, error: undefined };
        }
        return item;
    }));
    
    let successCount = 0;
    let failCount = 0;

    try {
      const presignPayload = {
        prefix,
        files: filesToReplace.map(f => ({
          filename: f.name,
          content_type: getContentType(f.file),
          overwrite: true
        }))
      };
      const presignRes = await request.post("/web-twinprocms/s3-file/presign", presignPayload);
      const presignedUrls = presignRes.data?.data || [];
      const urlMap: Record<string, { url: string; key: string }> = {};
      presignedUrls.forEach((item: any) => {
        urlMap[item.filename] = { url: item.upload_url, key: item.key };
      });
      
      for (const entry of filesToReplace) {
        if (cancelAllRef.current) break;
        const uploadUrlInfo = urlMap[entry.name];
        if (!uploadUrlInfo) {
          setFileList(prev => prev.map(item => item.id === entry.id ? { ...item, status: "error", error: "Missing presigned URL" } : item));
          failCount++;
          continue;
        }
        const success = await processS3Upload(entry, uploadUrlInfo);
        if (success) successCount++; else failCount++;
      }
    } catch (err: any) {
      const errMsg = err.response?.data?.error || err.message || "Replace All failed";
      setFileList(prev => prev.map(item => {
        if (filesToReplace.find(f => f.id === item.id)) {
          return { ...item, status: "error", error: errMsg };
        }
        return item;
      }));
      failCount += filesToReplace.length;
    }
    
    setIsUploading(false);
    
    if (successCount > 0) {
      toast.success(`${successCount} file(s) replaced successfully.`);
      refetch();
    }
  };

  const handleCancelAll = () => {
    cancelAllRef.current = true;
    fileList.forEach(entry => {
      if ((entry.status === "uploading" || entry.status === "extracting") && entry.abortController) {
        entry.abortController.abort();
      }
    });
  };

  return (
    <>
      <Modal
        open={isOpen}
        title="Upload Files"
        onOk={handleClose}
        onCancel={handleModalCancel}
        footer={[]}
        width={600}
        className={styles["add-file-modal"]}
        closable={!isBusy}
        maskClosable={!isBusy}
        keyboard={!isBusy}
      >
        <div className={styles["upload-zone-wrapper"]}>
          <Dragger
            multiple
            beforeUpload={handleBeforeUpload}
            showUploadList={false}
            disabled={isBusy}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag files to this area</p>
            <p className="ant-upload-hint">
              Supports single or bulk upload.
            </p>
          </Dragger>
          <div style={{ marginTop: '8px', color: 'rgba(255, 255, 255, 0.45)', fontSize: '12px', textAlign: 'center' }}>
            Accepted: {ALLOWED_UPLOAD_EXTENSIONS.map(ext => ext.replace(".", "").toUpperCase()).join(", ")}
          </div>
        </div>

        {fileList.length > 0 && (
          <div className={styles["file-list-container"]}>
            <div className={styles["file-list-header"]}>
              <span>Selected Files</span>
              <span>
                {fileList.filter((f) => f.status === "done").length} of{" "}
                {fileList.length} uploaded
              </span>
            </div>
            <div className={styles["file-list"]}>
              {fileList.map((entry) => (
                <div key={entry.id} className={styles["file-item"]}>
                  <div className={styles["file-row-main"]}>
                    <div className={styles["file-info"]}>
                      <FileOutlined className={styles["file-icon"]} />
                      <span className={styles["file-name"]} title={entry.name}>
                        {entry.name}
                      </span>
                      <span
                        className={`${styles["file-status"]} ${
                          styles[`status-${entry.status}`]
                        }`}
                      >
                        {getStatusText(entry.status)}
                      </span>
                    </div>

                    <div className={styles["file-actions"]}>
                      {(entry.status === "uploading" || entry.status === "extracting") && (
                        <button
                          className={styles["action-btn"]}
                          onClick={() => {
                            if (entry.abortController) entry.abortController.abort();
                          }}
                          title="Cancel upload"
                        >
                          <StopOutlined />
                        </button>
                      )}
                      {entry.status === "collision" && (
                        <button
                          className={`${styles["action-btn"]} ${styles["replace-btn"]}`}
                          onClick={() => setReplaceConfirmEntry(entry)}
                          disabled={isUploading}
                          title="Replace existing file"
                        >
                          <SwapOutlined />
                        </button>
                      )}
                      {entry.status === "error" && (
                        <button
                          className={`${styles["action-btn"]} ${styles["retry-btn"]}`}
                          onClick={() => uploadSingleFile(entry)}
                          disabled={isUploading}
                          title="Retry upload"
                        >
                          <ReloadOutlined />
                        </button>
                      )}
                      {(entry.status === "pending" ||
                        entry.status === "error" ||
                        entry.status === "cancelled" ||
                        entry.status === "collision") && (
                        <button
                          className={styles["action-btn"]}
                          onClick={() => handleRemoveFile(entry.id)}
                          disabled={isUploading}
                          title="Remove file"
                        >
                          <CloseOutlined />
                        </button>
                      )}
                    </div>
                  </div>

                  {entry.isZip && !entry.zipChoiceMade && entry.status !== "extracting" && (
                    <div className={styles["zip-actions"]}>
                      <span className={styles["zip-actions-text"]}>
                        This is a ZIP archive. How would you like to upload it?
                      </span>
                      <Button
                        size="small"
                        onClick={() => handleZipAsIs(entry.id)}
                      >
                        Upload As-Is
                      </Button>
                      <Button
                        size="small"
                        type="primary"
                        onClick={() => handleZipExtract(entry)}
                      >
                        Extract
                      </Button>
                    </div>
                  )}

                  {(entry.status === "uploading" ||
                    entry.status === "extracting" ||
                    entry.status === "done" ||
                    entry.status === "error") && (
                    <div className={styles["progress-container"]}>
                      <Progress
                        percent={entry.progress}
                        size="small"
                        status={
                          entry.status === "error"
                            ? "exception"
                            : entry.status === "done"
                            ? "success"
                            : "active"
                        }
                        showInfo={false}
                      />
                    </div>
                  )}

                  {(entry.status === "error" || entry.status === "cancelled" || entry.status === "collision") && entry.error && (
                    <div className={styles["error-text"]}>{entry.error}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end pt-4">
          <Button
            className="bg-[#3d4e65] text-white mr-4"
            onClick={handleModalCancel}
            disabled={isBusy && !isUploading} // Allow closing if just uploading, wait it prevents close?
            // We keep disabled={isBusy} for the Close button, but let's just make it disabled if extracting.
            style={{ backgroundColor: '#3d4e65', color: 'white', border: 'none' }}
          >
            Close
          </Button>
          {isUploading ? (
            <Button
              danger
              onClick={handleCancelAll}
              className="text-white bg-red-500"
              style={{ backgroundColor: '#ff4d4f', color: 'white', border: 'none' }}
            >
              Cancel All
            </Button>
          ) : (
            <Button
              type="primary"
              onClick={handleUploadAll}
              disabled={!canUpload}
              className="bg-[#299aff] text-white"
              style={{ backgroundColor: '#299aff', color: 'white', border: 'none' }}
            >
              Upload All
            </Button>
          )}
        </div>
      </Modal>

      <Modal
        open={!!replaceConfirmEntry}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#fa8c16' }}>
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>Replace existing file?</span>
          </div>
        }
        onOk={() => {
          if (replaceConfirmEntry) handleReplaceFile(replaceConfirmEntry);
        }}
        onCancel={() => setReplaceConfirmEntry(null)}
        okText="Yes, replace it"
        okButtonProps={{ style: { backgroundColor: '#fa8c16', borderColor: '#fa8c16', color: 'white' } }}
        cancelButtonProps={{ style: { backgroundColor: '#3d4e65', color: 'white', border: 'none' } }}
        width={400}
        className={styles["add-file-modal"]}
      >
        <p style={{ marginTop: '16px' }}>
          This will permanently replace <strong>"{replaceConfirmEntry?.name}"</strong> in the file library. This action cannot be undone.
        </p>
      </Modal>
      <Modal
        open={batchCollisionFiles.length > 0}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#fa8c16' }}>
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>{batchCollisionFiles.length} file(s) already exist</span>
          </div>
        }
        onCancel={() => setBatchCollisionFiles([])}
        footer={[
          <Button key="skip" onClick={() => setBatchCollisionFiles([])} style={{ backgroundColor: '#3d4e65', color: 'white', border: 'none' }}>
            Skip All
          </Button>,
          <Button key="replace" danger onClick={() => setShowReplaceAllConfirm(true)} style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16', color: 'white' }}>
            Replace All
          </Button>
        ]}
        width={450}
        className={styles["add-file-modal"]}
      >
        <p style={{ marginTop: '16px', marginBottom: '8px', color: 'rgba(255, 255, 255, 0.85)' }}>
          The following files already exist in this folder. What would you like to do?
        </p>
        <ul style={{ maxHeight: '150px', overflowY: 'auto', background: 'rgba(5, 10, 31, 0.3)', padding: '8px 24px', borderRadius: '4px', color: 'rgba(255, 255, 255, 0.65)' }}>
          {batchCollisionFiles.map(f => (
            <li key={f.id} style={{ marginBottom: '4px' }}>{f.name}</li>
          ))}
        </ul>
      </Modal>

      <Modal
        open={showReplaceAllConfirm}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#fa8c16' }}>
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>Replace existing files?</span>
          </div>
        }
        onOk={handleReplaceAll}
        onCancel={() => setShowReplaceAllConfirm(false)}
        okText="Yes, replace them"
        okButtonProps={{ style: { backgroundColor: '#fa8c16', borderColor: '#fa8c16', color: 'white' } }}
        cancelButtonProps={{ style: { backgroundColor: '#3d4e65', color: 'white', border: 'none' } }}
        width={400}
        className={styles["add-file-modal"]}
      >
        <p style={{ marginTop: '16px' }}>
          This will permanently replace <strong>{batchCollisionFiles.length} file(s)</strong> in the file library. This action cannot be undone.
        </p>
      </Modal>
    </>
  );
};

export default AddFile;
